/* ═══════════════════════════════════════════════════
   CAREER DATABASE — v2 (Expanded to 20 careers)
   
   KEY FIX: Trait weights now form distinct vectors.
   Creative careers have high creative + low analytical.
   Technical careers have high technical + high analytical.
   This makes cosine similarity produce correct matches.
   
   RULE: Weights must sum to ~1.0 for each career.
   The DOMINANT trait(s) should be 0.30-0.45.
   Minor traits should be 0.05-0.15.
═══════════════════════════════════════════════════ */

const careers = [
  // ═══════════ CREATIVE CAREERS ═══════════
  {
    id: "graphic-designer",
    title: "Graphic / UI-UX Designer",
    emoji: "🎨",
    tagline: "Design experiences people love to use",
    color: "#A29BFE",
    // HIGH creative, moderate technical, low analytical
    traits: {
      creative: 0.45,
      technical: 0.2,
      business: 0.15,
      social: 0.12,
      analytical: 0.08,
    },
    description:
      "Designers shape how the world looks and feels — from apps to brand logos. UI/UX Designers combine art and psychology to create beautiful, intuitive products.",
    whatTheyDo: [
      "Design app interfaces, websites, and brand identities",
      "Create user experience (UX) flows and prototypes",
      "Work with developers to bring designs to life",
      "Conduct user research and usability testing",
      "Create visual content for marketing and social media",
    ],
    skills: [
      "Figma / Adobe XD",
      "Color Theory & Typography",
      "User Psychology",
      "HTML/CSS basics",
      "Illustration",
      "Communication",
    ],
    salaryRange: {
      entry: "₹3-8 LPA",
      mid: "₹10-25 LPA",
      senior: "₹30-60+ LPA",
    },
    topColleges: [
      "NID Ahmedabad",
      "IDC IIT Bombay",
      "Srishti Bangalore",
      "MIT Institute of Design",
      "Pearl Academy",
    ],
    roadmap: [
      { step: "After 10th", detail: "Any stream works — Science or Arts" },
      { step: "After 12th", detail: "B.Des in Communication/UI-UX or BFA" },
      {
        step: "During College",
        detail: "Build a portfolio, learn Figma, freelance",
      },
      {
        step: "After Graduation",
        detail: "Join as UI/UX Designer at startups or agencies",
      },
      {
        step: "5+ Years",
        detail: "Design Lead, Creative Director, or start a studio",
      },
    ],
    growthOutlook:
      "Every digital product needs designers. India's design industry growing 30%+ yearly.",
    whyMatch:
      "Your creative instinct and visual thinking make you a natural designer. You see beauty where others see blank canvases.",
  },
  {
    id: "animator",
    title: "Animator / Motion Designer",
    emoji: "🎬",
    tagline: "Bring characters and stories to life",
    color: "#E17055",
    traits: {
      creative: 0.5,
      technical: 0.2,
      social: 0.1,
      analytical: 0.1,
      business: 0.1,
    },
    description:
      "Animators create the magic behind movies, games, and ads. From Pixar films to Instagram reels — animation is everywhere. If you love drawing and storytelling, this is your world.",
    whatTheyDo: [
      "Create 2D/3D animations for films, games, and ads",
      "Design characters, backgrounds, and visual effects",
      "Use tools like Blender, Maya, After Effects",
      "Storyboard scenes and plan visual narratives",
      "Work with directors and creative teams",
    ],
    skills: [
      "Drawing & Sketching",
      "Blender / Maya / After Effects",
      "Storytelling",
      "Color & Composition",
      "Patience & Detail",
      "Creativity",
    ],
    salaryRange: { entry: "₹3-7 LPA", mid: "₹8-20 LPA", senior: "₹25-50+ LPA" },
    topColleges: [
      "NID Ahmedabad",
      "IDC IIT Bombay",
      "Whistling Woods",
      "Arena Animation",
      "MAAC",
    ],
    roadmap: [
      {
        step: "After 10th",
        detail: "Arts or Science — start learning drawing now",
      },
      {
        step: "After 12th",
        detail: "B.Des Animation / BFA / Diploma in Animation",
      },
      {
        step: "During College",
        detail: "Create a showreel, learn Blender, freelance",
      },
      {
        step: "After Graduation",
        detail: "Join animation studios or VFX companies",
      },
      {
        step: "5+ Years",
        detail: "Lead Animator, Art Director, or own studio",
      },
    ],
    growthOutlook:
      "India's animation industry worth ₹10,000+ Cr. Netflix, YouTube, gaming driving massive demand.",
    whyMatch:
      "Your imagination runs wild and you love bringing ideas to life visually. Animation turns your creativity into magic.",
  },
  {
    id: "content-creator",
    title: "Content Creator / YouTuber",
    emoji: "📹",
    tagline: "Create content that millions watch and love",
    color: "#FF6B6B",
    traits: {
      creative: 0.35,
      social: 0.25,
      business: 0.2,
      technical: 0.1,
      analytical: 0.1,
    },
    description:
      "Content creators build audiences and brands through videos, blogs, podcasts, and social media. In India, top creators earn more than many CEOs. If you love performing and creating, this is your stage.",
    whatTheyDo: [
      "Create videos, reels, podcasts, or written content",
      "Build and engage with online communities",
      "Collaborate with brands for sponsorships",
      "Edit videos and manage social media channels",
      "Stay on top of trends and create viral content",
    ],
    skills: [
      "Video Editing",
      "Storytelling",
      "Public Speaking",
      "Social Media Strategy",
      "Branding",
      "Consistency",
    ],
    salaryRange: {
      entry: "₹1-5 LPA",
      mid: "₹10-30 LPA",
      senior: "₹50L-5Cr+ LPA",
    },
    topColleges: [
      "Any college + self-learning",
      "Mass Communication colleges",
      "Film schools",
    ],
    roadmap: [
      { step: "After 10th", detail: "Any stream — start creating content NOW" },
      {
        step: "After 12th",
        detail: "BA Mass Communication / BMM / start full-time",
      },
      {
        step: "During College",
        detail: "Build audience, learn editing, collaborate",
      },
      {
        step: "After Graduation",
        detail: "Full-time creator or join media companies",
      },
      {
        step: "3+ Years",
        detail: "Brand deals, own production house, diversify",
      },
    ],
    growthOutlook:
      "India's creator economy worth $15B+ by 2030. 80+ million creators in India.",
    whyMatch:
      "Your creative energy and social skills make you a natural content creator. You know how to capture attention.",
  },
  {
    id: "fashion-designer",
    title: "Fashion Designer",
    emoji: "👗",
    tagline: "Design the clothes the world wants to wear",
    color: "#FD79A8",
    traits: {
      creative: 0.45,
      business: 0.2,
      social: 0.15,
      technical: 0.12,
      analytical: 0.08,
    },
    description:
      "Fashion designers create clothing, accessories, and trends that define culture. From luxury brands to sustainable fashion — if you have an eye for style and fabrics excite you, this is your runway.",
    whatTheyDo: [
      "Design clothing collections and accessories",
      "Select fabrics, colors, and patterns",
      "Create sketches and technical drawings",
      "Manage production and quality control",
      "Present collections at fashion shows",
    ],
    skills: [
      "Sketching & Illustration",
      "Fabric Knowledge",
      "Color Theory",
      "Sewing & Pattern Making",
      "Trend Forecasting",
      "Branding",
    ],
    salaryRange: { entry: "₹3-6 LPA", mid: "₹8-20 LPA", senior: "₹25-60+ LPA" },
    topColleges: [
      "NIFT Delhi",
      "Pearl Academy",
      "NID Ahmedabad",
      "JD Institute",
      "INIFD",
    ],
    roadmap: [
      {
        step: "After 10th",
        detail: "Any stream — start sketching fashion designs",
      },
      { step: "After 12th", detail: "B.Des Fashion Design (NIFT entrance)" },
      {
        step: "During College",
        detail: "Intern with designers, build a portfolio",
      },
      {
        step: "After Graduation",
        detail: "Join fashion houses or start your label",
      },
      {
        step: "5+ Years",
        detail: "Creative Director, own brand, fashion shows",
      },
    ],
    growthOutlook:
      "India's fashion market worth ₹7 lakh Cr. Sustainable fashion is the new boom.",
    whyMatch:
      "Your creative vision and eye for aesthetics are perfect for fashion. You think in colors, textures, and style.",
  },
  {
    id: "game-designer",
    title: "Game Designer / Developer",
    emoji: "🎮",
    tagline: "Build worlds that millions play in",
    color: "#6C5CE7",
    traits: {
      creative: 0.3,
      technical: 0.3,
      analytical: 0.15,
      business: 0.15,
      social: 0.1,
    },
    description:
      "Game designers create the rules, stories, and experiences of video games. From mobile games to AAA titles — you combine creativity with technology to build immersive worlds.",
    whatTheyDo: [
      "Design game mechanics, levels, and storylines",
      "Create game art, characters, and environments",
      "Program game logic using Unity or Unreal Engine",
      "Test and balance gameplay for fun factor",
      "Work with artists, writers, and programmers",
    ],
    skills: [
      "Game Engines (Unity/Unreal)",
      "Programming (C#/C++)",
      "3D Modeling",
      "Storytelling",
      "Level Design",
      "Player Psychology",
    ],
    salaryRange: {
      entry: "₹4-8 LPA",
      mid: "₹12-30 LPA",
      senior: "₹35-70+ LPA",
    },
    topColleges: [
      "IIT Bombay",
      "IIIT Hyderabad",
      "Backstage Pass (Hyderabad)",
      "Gametion Academy",
    ],
    roadmap: [
      { step: "After 10th", detail: "Science (PCM) with Computer Science" },
      { step: "After 12th", detail: "B.Tech CS or B.Des Game Design" },
      {
        step: "During College",
        detail: "Build games in Unity, join game jams",
      },
      { step: "After Graduation", detail: "Join gaming studios or go indie" },
      { step: "5+ Years", detail: "Lead Designer, start your own game studio" },
    ],
    growthOutlook:
      "India's gaming market worth $7.5B+ by 2027. Massive growth in mobile and esports.",
    whyMatch:
      "You blend creativity with technical thinking — the exact combo game design needs.",
  },

  // ═══════════ TECHNICAL CAREERS ═══════════
  {
    id: "ai-engineer",
    title: "AI / ML Engineer",
    emoji: "🤖",
    tagline: "Build the brains behind smart machines",
    color: "#6C5CE7",
    // HIGH analytical + technical, LOW creative
    traits: {
      analytical: 0.35,
      technical: 0.35,
      creative: 0.12,
      business: 0.1,
      social: 0.08,
    },
    description:
      "AI Engineers design systems that learn and think — from voice assistants to self-driving cars. You'll work with data, algorithms, and cutting-edge technology.",
    whatTheyDo: [
      "Build machine learning models that learn from data",
      "Create chatbots, recommendation systems, and image recognizers",
      "Work with massive datasets to find patterns",
      "Develop AI solutions for healthcare, finance, education",
      "Research new AI techniques",
    ],
    skills: [
      "Mathematics & Statistics",
      "Python Programming",
      "Machine Learning",
      "Data Analysis",
      "Problem Solving",
      "Linear Algebra",
    ],
    salaryRange: {
      entry: "₹6-12 LPA",
      mid: "₹15-35 LPA",
      senior: "₹40-80+ LPA",
    },
    topColleges: [
      "IIT Bombay",
      "IIT Delhi",
      "IIT Madras",
      "IIIT Hyderabad",
      "IISc Bangalore",
    ],
    roadmap: [
      {
        step: "After 10th",
        detail: "Science stream (PCM) with Computer Science",
      },
      { step: "After 12th", detail: "B.Tech in CS/AI/Data Science" },
      { step: "During College", detail: "Learn Python, ML, compete on Kaggle" },
      {
        step: "After Graduation",
        detail: "M.Tech/MS in AI or join as ML Engineer",
      },
      { step: "5+ Years", detail: "Lead AI teams or start AI company" },
    ],
    growthOutlook:
      "Fastest growing field. India needs 1M+ AI professionals by 2030.",
    whyMatch:
      "Your analytical mind and technical curiosity are perfect for AI. You love solving complex puzzles with data.",
  },
  {
    id: "software-developer",
    title: "Software Developer",
    emoji: "💻",
    tagline: "Create apps that millions use daily",
    color: "#00B894",
    traits: {
      technical: 0.4,
      analytical: 0.25,
      creative: 0.15,
      business: 0.12,
      social: 0.08,
    },
    description:
      "Software Developers write the code powering everything — from Instagram to banking apps. If you love building things and solving puzzles with logic, this is your playground.",
    whatTheyDo: [
      "Write code to build websites, mobile apps, and software",
      "Debug and fix issues in existing systems",
      "Design system architecture for scalable apps",
      "Collaborate with designers and product teams",
      "Learn new technologies constantly",
    ],
    skills: [
      "Programming (Java, Python, JavaScript)",
      "Data Structures & Algorithms",
      "Web/App Development",
      "Database Management",
      "Git & Version Control",
      "Problem Solving",
    ],
    salaryRange: {
      entry: "₹4-10 LPA",
      mid: "₹12-30 LPA",
      senior: "₹35-70+ LPA",
    },
    topColleges: [
      "IIT Bombay",
      "IIT Delhi",
      "NIT Warangal",
      "BITS Pilani",
      "DTU Delhi",
    ],
    roadmap: [
      {
        step: "After 10th",
        detail: "Science stream with Maths and Computer Science",
      },
      { step: "After 12th", detail: "B.Tech in Computer Science or BCA" },
      {
        step: "During College",
        detail: "Practice on LeetCode, build projects, intern",
      },
      { step: "After Graduation", detail: "Join as Software Engineer" },
      { step: "5+ Years", detail: "Tech Lead, Architect, or startup founder" },
    ],
    growthOutlook:
      "India's IT industry worth $250B+. Software devs always in demand.",
    whyMatch:
      "Your technical mind loves building things. Code is your tool, logic is your superpower.",
  },
  {
    id: "data-scientist",
    title: "Data Scientist",
    emoji: "📊",
    tagline: "Turn data into decisions that change everything",
    color: "#0984E3",
    traits: {
      analytical: 0.4,
      technical: 0.25,
      business: 0.2,
      creative: 0.08,
      social: 0.07,
    },
    description:
      "Data Scientists analyze massive datasets to find patterns, predict trends, and help businesses make smarter decisions. Called 'the sexiest job of the 21st century'.",
    whatTheyDo: [
      "Analyze large datasets to find insights",
      "Build predictive models and dashboards",
      "Help companies make data-driven decisions",
      "Work with AI and machine learning tools",
      "Present findings to business leaders",
    ],
    skills: [
      "Statistics & Probability",
      "Python/R Programming",
      "SQL & Databases",
      "Data Visualization",
      "Machine Learning",
      "Business Acumen",
    ],
    salaryRange: {
      entry: "₹6-14 LPA",
      mid: "₹18-40 LPA",
      senior: "₹45-80+ LPA",
    },
    topColleges: [
      "ISI Kolkata",
      "IIT Madras",
      "IIM Calcutta",
      "IIIT Hyderabad",
      "CMI Chennai",
    ],
    roadmap: [
      { step: "After 10th", detail: "Science stream with Mathematics" },
      {
        step: "After 12th",
        detail: "B.Tech CS / BSc Statistics / BSc Data Science",
      },
      { step: "During College", detail: "Learn Python, SQL, Tableau" },
      {
        step: "After Graduation",
        detail: "MS in Data Science or join as Analyst",
      },
      { step: "5+ Years", detail: "Lead Data Scientist, Chief Data Officer" },
    ],
    growthOutlook: "India's data science market growing 40%+ annually.",
    whyMatch:
      "Your analytical mind sees patterns in chaos. Data is your language, insights are your art.",
  },
  {
    id: "cybersecurity",
    title: "Cybersecurity Expert",
    emoji: "🛡️",
    tagline: "Protect the digital world from hackers",
    color: "#2D3436",
    traits: {
      technical: 0.35,
      analytical: 0.35,
      business: 0.12,
      creative: 0.1,
      social: 0.08,
    },
    description:
      "Cybersecurity experts protect companies, governments, and people from hackers and cyber threats. With India going digital, this is one of the most critical and exciting careers.",
    whatTheyDo: [
      "Find and fix security vulnerabilities in systems",
      "Investigate cyber attacks and data breaches",
      "Build security systems and firewalls",
      "Ethical hacking and penetration testing",
      "Advise organizations on security policies",
    ],
    skills: [
      "Networking",
      "Linux/OS knowledge",
      "Programming (Python/C)",
      "Ethical Hacking",
      "Cryptography",
      "Critical Thinking",
    ],
    salaryRange: {
      entry: "₹5-10 LPA",
      mid: "₹15-35 LPA",
      senior: "₹40-80+ LPA",
    },
    topColleges: [
      "IIT Kanpur",
      "IIT Madras",
      "IIIT Delhi",
      "Amrita University",
    ],
    roadmap: [
      { step: "After 10th", detail: "Science (PCM) with Computer Science" },
      { step: "After 12th", detail: "B.Tech CS with security specialization" },
      {
        step: "During College",
        detail: "CTF competitions, bug bounties, certifications",
      },
      { step: "After Graduation", detail: "Join as Security Analyst" },
      { step: "5+ Years", detail: "CISO, Security Architect, or consultant" },
    ],
    growthOutlook:
      "India needs 1.5 million cybersecurity professionals. Huge shortage = huge opportunity.",
    whyMatch:
      "Your technical + analytical combo is perfect for security. You think like a detective.",
  },

  // ═══════════ SOCIAL CAREERS ═══════════
  {
    id: "doctor",
    title: "Doctor / Medical Professional",
    emoji: "🩺",
    tagline: "Save lives and heal the world",
    color: "#E17055",
    traits: {
      social: 0.3,
      analytical: 0.3,
      technical: 0.18,
      creative: 0.1,
      business: 0.12,
    },
    description:
      "Doctors diagnose diseases, treat patients, and save lives. One of India's most respected professions requiring dedication, empathy, and sharp analytical skills.",
    whatTheyDo: [
      "Diagnose illnesses and prescribe treatment",
      "Perform surgeries and procedures",
      "Research new treatments and medicines",
      "Counsel patients and families",
      "Work in hospitals, clinics, or private practice",
    ],
    skills: [
      "Biology & Chemistry",
      "Anatomy",
      "Critical Thinking",
      "Empathy",
      "Steady Hands",
      "Continuous Learning",
    ],
    salaryRange: {
      entry: "₹6-12 LPA",
      mid: "₹15-40 LPA",
      senior: "₹50L-2Cr+ LPA",
    },
    topColleges: [
      "AIIMS Delhi",
      "CMC Vellore",
      "JIPMER",
      "Maulana Azad Medical",
      "KEM Mumbai",
    ],
    roadmap: [
      {
        step: "After 10th",
        detail: "Science (PCB — Physics, Chemistry, Biology)",
      },
      { step: "After 12th", detail: "Clear NEET → MBBS (5.5 years)" },
      { step: "After MBBS", detail: "Internship, then practice or specialize" },
      { step: "Specialization", detail: "MD/MS (3 years)" },
      {
        step: "10+ Years",
        detail: "Specialist, Hospital Head, or Private Practice",
      },
    ],
    growthOutlook:
      "India needs 6 lakh more doctors. Healthcare is recession-proof.",
    whyMatch:
      "Your social empathy combined with analytical thinking makes you perfect for healing people.",
  },
  {
    id: "psychologist",
    title: "Psychologist / Counselor",
    emoji: "🧠",
    tagline: "Understand minds and heal hearts",
    color: "#00CEC9",
    traits: {
      social: 0.42,
      analytical: 0.22,
      creative: 0.15,
      business: 0.12,
      technical: 0.09,
    },
    description:
      "Psychologists help people understand their emotions and overcome challenges. With rising mental health awareness in India, this is one of the most impactful careers.",
    whatTheyDo: [
      "Counsel individuals with stress, anxiety, depression",
      "Conduct psychological assessments and therapy",
      "Work with schools, hospitals, and corporates",
      "Research human behavior",
      "Help companies with employee wellbeing",
    ],
    skills: [
      "Empathy",
      "Active Listening",
      "Research",
      "Communication",
      "Patience",
      "Emotional Intelligence",
    ],
    salaryRange: {
      entry: "₹3-8 LPA",
      mid: "₹10-25 LPA",
      senior: "₹30-60+ LPA",
    },
    topColleges: [
      "TISS Mumbai",
      "JMI Delhi",
      "Christ University",
      "Delhi University",
      "Ashoka",
    ],
    roadmap: [
      { step: "After 10th", detail: "Arts/Science — Psychology if available" },
      { step: "After 12th", detail: "BA/BSc Psychology" },
      { step: "After Graduation", detail: "MA/MSc Psychology" },
      {
        step: "Certification",
        detail: "RCI registration for clinical practice",
      },
      {
        step: "5+ Years",
        detail: "Senior Therapist, private practice, consulting",
      },
    ],
    growthOutlook:
      "Mental health awareness exploding. Demand growing 20%+ yearly.",
    whyMatch:
      "Your deep empathy and desire to help others makes psychology your calling.",
  },
  {
    id: "teacher",
    title: "Teacher / Education Leader",
    emoji: "🧑‍🏫",
    tagline: "Shape minds and change futures",
    color: "#55A3F0",
    traits: {
      social: 0.4,
      creative: 0.18,
      analytical: 0.18,
      business: 0.12,
      technical: 0.12,
    },
    description:
      "Teachers are the foundation of every career. From village schools to IITs, great teachers change lives. If you love explaining, mentoring, and watching people grow — this is your purpose.",
    whatTheyDo: [
      "Plan and deliver engaging lessons",
      "Mentor and guide students",
      "Create educational content and resources",
      "Assess student progress and provide feedback",
      "Lead educational programs and initiatives",
    ],
    skills: [
      "Communication",
      "Patience",
      "Subject Expertise",
      "Creativity in Teaching",
      "Empathy",
      "Leadership",
    ],
    salaryRange: { entry: "₹3-6 LPA", mid: "₹8-20 LPA", senior: "₹25-50+ LPA" },
    topColleges: [
      "Any university for B.Ed",
      "NCERT",
      "Azim Premji University",
      "Teach For India",
    ],
    roadmap: [
      { step: "After 10th", detail: "Any stream based on subject interest" },
      { step: "After 12th", detail: "BA/BSc in your subject + B.Ed" },
      { step: "After B.Ed", detail: "Join school as teacher, clear TET/CTET" },
      { step: "5+ Years", detail: "Senior Teacher, HOD, Vice Principal" },
      { step: "10+ Years", detail: "Principal, Education Consultant, EdTech" },
    ],
    growthOutlook:
      "India has 10 million+ teaching positions. EdTech creating new opportunities.",
    whyMatch:
      "Your social nature and love for helping people learn makes teaching your natural gift.",
  },
  {
    id: "social-worker",
    title: "Social Worker / NGO Leader",
    emoji: "🌍",
    tagline: "Change the world one community at a time",
    color: "#00B894",
    traits: {
      social: 0.45,
      business: 0.15,
      creative: 0.15,
      analytical: 0.15,
      technical: 0.1,
    },
    description:
      "Social workers fight for justice, equality, and community welfare. From rural development to policy making — if you want to create real social change, this career gives you that power.",
    whatTheyDo: [
      "Work with communities on health, education, poverty",
      "Advocate for rights of marginalized groups",
      "Design and implement social programs",
      "Partner with government and NGOs",
      "Research social issues and policy solutions",
    ],
    skills: [
      "Empathy",
      "Leadership",
      "Communication",
      "Project Management",
      "Research",
      "Resilience",
    ],
    salaryRange: { entry: "₹3-6 LPA", mid: "₹8-18 LPA", senior: "₹20-40+ LPA" },
    topColleges: [
      "TISS Mumbai",
      "Delhi School of Social Work",
      "Loyola College",
      "MSW programs",
    ],
    roadmap: [
      {
        step: "After 10th",
        detail: "Arts stream — Sociology, Political Science",
      },
      { step: "After 12th", detail: "BA Social Work or BA Sociology" },
      { step: "After Graduation", detail: "MSW (Master of Social Work)" },
      {
        step: "After MSW",
        detail: "Join NGOs, government, or international orgs",
      },
      {
        step: "10+ Years",
        detail: "NGO Director, Policy Maker, UN/WHO consultant",
      },
    ],
    growthOutlook:
      "India's social sector growing rapidly with CSR mandates and global funding.",
    whyMatch:
      "Your deep empathy and social awareness drives you to make the world better.",
  },

  // ═══════════ BUSINESS CAREERS ═══════════
  {
    id: "chartered-accountant",
    title: "Chartered Accountant",
    emoji: "📋",
    tagline: "Master the language of business and money",
    color: "#FDCB6E",
    traits: {
      business: 0.35,
      analytical: 0.35,
      technical: 0.1,
      social: 0.12,
      creative: 0.08,
    },
    description:
      "CAs are the financial guardians of every business. From auditing to taxation — CAs are essential everywhere. Incredible stability and prestige in India.",
    whatTheyDo: [
      "Audit financial statements",
      "Handle taxation and compliance",
      "Advise businesses on financial planning",
      "Manage budgets and strategies",
      "Work in consulting, banking, or own practice",
    ],
    skills: [
      "Accounting & Finance",
      "Taxation",
      "Auditing",
      "Analytical Thinking",
      "Attention to Detail",
      "Communication",
    ],
    salaryRange: {
      entry: "₹7-12 LPA",
      mid: "₹15-35 LPA",
      senior: "₹40-1Cr+ LPA",
    },
    topColleges: [
      "ICAI",
      "SRCC Delhi",
      "St. Xavier's Mumbai",
      "Christ University",
    ],
    roadmap: [
      { step: "After 10th", detail: "Commerce with Accountancy and Maths" },
      { step: "After 12th", detail: "CA Foundation (ICAI)" },
      { step: "CA Inter", detail: "Clear Intermediate exam" },
      { step: "Articleship", detail: "3 years practical training" },
      { step: "CA Final", detail: "Clear Final → qualified CA" },
    ],
    growthOutlook:
      "Every company needs CAs. GST and digital economy boosting demand.",
    whyMatch:
      "Your business mind and analytical precision make you a natural CA. Numbers speak to you.",
  },
  {
    id: "entrepreneur",
    title: "Entrepreneur / Startup Founder",
    emoji: "🚀",
    tagline: "Build something the world has never seen",
    color: "#FF6B6B",
    traits: {
      business: 0.4,
      creative: 0.2,
      social: 0.18,
      analytical: 0.12,
      technical: 0.1,
    },
    description:
      "Entrepreneurs create businesses from ideas. India's startup ecosystem is on fire. If you see problems as opportunities and love taking risks, this is your destiny.",
    whatTheyDo: [
      "Identify market problems and create solutions",
      "Build and lead teams",
      "Raise funding from investors",
      "Make strategic decisions",
      "Scale businesses from 0 to millions of users",
    ],
    skills: [
      "Leadership",
      "Risk-Taking",
      "Financial Literacy",
      "Marketing",
      "Negotiation",
      "Resilience",
    ],
    salaryRange: {
      entry: "₹0 (invest first!)",
      mid: "₹20-50 LPA (if successful)",
      senior: "Unlimited 🚀",
    },
    topColleges: [
      "IIM Ahmedabad",
      "IIM Bangalore",
      "ISB Hyderabad",
      "BITS Pilani",
    ],
    roadmap: [
      { step: "After 10th", detail: "Commerce or Science" },
      {
        step: "After 12th",
        detail: "BBA, B.Com, or B.Tech — start side projects",
      },
      { step: "During College", detail: "Start small business, join E-Cell" },
      {
        step: "After Graduation",
        detail: "Work 2-3 years to learn, then launch",
      },
      { step: "5+ Years", detail: "Scale startup, raise funding, build brand" },
    ],
    growthOutlook:
      "India has 100+ unicorn startups. Startup India initiative boosting ecosystem.",
    whyMatch:
      "Your business instinct and risk-taking ability scream entrepreneur. You're born to lead.",
  },
  {
    id: "marketing-manager",
    title: "Marketing / Brand Manager",
    emoji: "📢",
    tagline: "Make brands that people love and remember",
    color: "#E84393",
    traits: {
      business: 0.3,
      creative: 0.25,
      social: 0.25,
      analytical: 0.12,
      technical: 0.08,
    },
    description:
      "Marketing managers build brand stories, run campaigns, and drive sales. From Zomato's viral tweets to Apple's launches — great marketing is art + business combined.",
    whatTheyDo: [
      "Develop marketing strategies and campaigns",
      "Manage brand identity and messaging",
      "Analyze market data and consumer behavior",
      "Run digital marketing and social media",
      "Collaborate with creative and sales teams",
    ],
    skills: [
      "Communication",
      "Creativity",
      "Data Analysis",
      "Social Media",
      "Branding",
      "Consumer Psychology",
    ],
    salaryRange: {
      entry: "₹4-8 LPA",
      mid: "₹12-30 LPA",
      senior: "₹35-70+ LPA",
    },
    topColleges: [
      "IIM Ahmedabad",
      "MICA Ahmedabad",
      "XLRI",
      "FMS Delhi",
      "SP Jain",
    ],
    roadmap: [
      { step: "After 10th", detail: "Commerce or Arts" },
      { step: "After 12th", detail: "BBA / B.Com / BA Mass Communication" },
      {
        step: "During College",
        detail: "Run social media for clubs, intern at agencies",
      },
      {
        step: "After Graduation",
        detail: "MBA Marketing or join as Marketing Exec",
      },
      {
        step: "5+ Years",
        detail: "Brand Manager, CMO, start marketing agency",
      },
    ],
    growthOutlook:
      "Digital marketing growing 30%+ yearly. Every company needs marketers.",
    whyMatch:
      "Your blend of creativity and business sense makes you a natural marketer. You understand people AND strategy.",
  },
  {
    id: "investment-banker",
    title: "Investment Banker / Finance",
    emoji: "💹",
    tagline: "Move millions and shape the economy",
    color: "#2D3436",
    traits: {
      business: 0.35,
      analytical: 0.35,
      social: 0.12,
      technical: 0.1,
      creative: 0.08,
    },
    description:
      "Investment bankers help companies raise money, merge, and grow. They work with billions of rupees and shape the economy. High-pressure, high-reward career for sharp minds.",
    whatTheyDo: [
      "Advise companies on mergers and acquisitions",
      "Help companies raise capital through IPOs",
      "Analyze financial markets and investments",
      "Build financial models and valuations",
      "Work on high-stakes corporate deals",
    ],
    skills: [
      "Financial Modeling",
      "Accounting",
      "Excel & Data Analysis",
      "Communication",
      "Attention to Detail",
      "High-Pressure Performance",
    ],
    salaryRange: {
      entry: "₹8-15 LPA",
      mid: "₹25-60 LPA",
      senior: "₹1-5Cr+ LPA",
    },
    topColleges: [
      "IIM Ahmedabad",
      "IIM Bangalore",
      "SRCC Delhi",
      "ISB Hyderabad",
    ],
    roadmap: [
      { step: "After 10th", detail: "Commerce with Maths" },
      { step: "After 12th", detail: "B.Com / BBA from top college" },
      { step: "During College", detail: "CFA prep, finance internships" },
      { step: "After Graduation", detail: "MBA Finance or join bank directly" },
      { step: "5+ Years", detail: "VP, Director, Managing Director" },
    ],
    growthOutlook:
      "India's financial sector booming with startup IPOs and foreign investment.",
    whyMatch:
      "Your analytical mind and business instinct make you perfect for high-stakes finance.",
  },

  // ═══════════ SCIENCE CAREERS ═══════════
  {
    id: "research-scientist",
    title: "Research Scientist",
    emoji: "🔬",
    tagline: "Discover the unknown and push human knowledge forward",
    color: "#00CEC9",
    traits: {
      analytical: 0.42,
      technical: 0.2,
      creative: 0.15,
      social: 0.13,
      business: 0.1,
    },
    description:
      "Research scientists explore the frontiers of knowledge — from new medicines to quantum physics. If curiosity drives you and you love asking 'why', this is your path to discovery.",
    whatTheyDo: [
      "Design and conduct scientific experiments",
      "Analyze data and publish findings",
      "Work in labs at universities or companies",
      "Collaborate with global research teams",
      "Apply for grants and funding",
    ],
    skills: [
      "Scientific Method",
      "Research & Analysis",
      "Statistics",
      "Technical Writing",
      "Curiosity",
      "Patience",
    ],
    salaryRange: {
      entry: "₹5-10 LPA",
      mid: "₹12-30 LPA",
      senior: "₹30-60+ LPA",
    },
    topColleges: [
      "IISc Bangalore",
      "TIFR Mumbai",
      "IIT Bombay",
      "JNU Delhi",
      "IISER Pune",
    ],
    roadmap: [
      { step: "After 10th", detail: "Science (PCM or PCB based on interest)" },
      { step: "After 12th", detail: "BSc / BS / Integrated MSc" },
      { step: "After Graduation", detail: "MSc + PhD in your field" },
      { step: "During PhD", detail: "Publish papers, present at conferences" },
      {
        step: "Post PhD",
        detail: "Research Professor, Lab Director, Industry R&D",
      },
    ],
    growthOutlook:
      "India investing heavily in R&D. ISRO, DRDO, private sector all hiring researchers.",
    whyMatch:
      "Your deep analytical curiosity drives you to uncover truths others miss.",
  },
  {
    id: "civil-services",
    title: "IAS / Civil Services Officer",
    emoji: "🏛️",
    tagline: "Serve the nation and shape India's future",
    color: "#E84393",
    traits: {
      social: 0.28,
      analytical: 0.22,
      business: 0.22,
      creative: 0.12,
      technical: 0.16,
    },
    description:
      "IAS officers are the backbone of Indian administration. They directly shape policies affecting 1.4 billion people. The ultimate career for those who want power to create real change.",
    whatTheyDo: [
      "Administer districts and implement schemes",
      "Draft policies and advise ministers",
      "Manage disaster relief and law & order",
      "Work across departments",
      "Represent India in international forums",
    ],
    skills: [
      "General Knowledge",
      "Writing",
      "Leadership",
      "Decision Making",
      "Empathy",
      "Current Affairs",
    ],
    salaryRange: {
      entry: "₹8-12 LPA + perks",
      mid: "₹15-25 LPA + housing",
      senior: "₹25-35 LPA + immense influence",
    },
    topColleges: [
      "Any graduation",
      "LBSNAA Mussoorie (training)",
      "Vajiram, Vision IAS",
    ],
    roadmap: [
      {
        step: "After 10th",
        detail: "Any stream — focus on reading and current affairs",
      },
      { step: "After 12th", detail: "Any graduation" },
      { step: "After Graduation", detail: "Prepare for UPSC CSE (1-3 years)" },
      { step: "Clear UPSC", detail: "Prelims → Mains → Interview" },
      { step: "Career", detail: "SDM → Collector → Secretary level" },
    ],
    growthOutlook:
      "800-1000 seats/year. Most prestigious career in Indian governance.",
    whyMatch:
      "Your balanced social, analytical, and leadership traits make you perfect for governance.",
  },
  {
    id: "lawyer",
    title: "Lawyer / Legal Professional",
    emoji: "⚖️",
    tagline: "Fight for justice and uphold the law",
    color: "#636E72",
    traits: {
      social: 0.25,
      analytical: 0.28,
      business: 0.22,
      creative: 0.13,
      technical: 0.12,
    },
    description:
      "Lawyers protect rights and voice justice. From Supreme Court arguments to corporate mergers — law offers a powerful career. Great for those who love debates and reading.",
    whatTheyDo: [
      "Represent clients in court",
      "Draft legal documents and contracts",
      "Advise businesses on legal compliance",
      "Work on human rights cases",
      "Specialize in corporate, criminal, or IP law",
    ],
    skills: [
      "Critical Thinking",
      "Public Speaking",
      "Research & Writing",
      "Negotiation",
      "Constitution Knowledge",
      "Patience",
    ],
    salaryRange: {
      entry: "₹5-10 LPA",
      mid: "₹15-40 LPA",
      senior: "₹50L-3Cr+ LPA",
    },
    topColleges: [
      "NLSIU Bangalore",
      "NALSAR Hyderabad",
      "NLU Delhi",
      "NLU Jodhpur",
    ],
    roadmap: [
      {
        step: "After 10th",
        detail: "Any stream — focus on English and Social Studies",
      },
      { step: "After 12th", detail: "5-year BA LLB via CLAT exam" },
      {
        step: "During College",
        detail: "Moot courts, internships at law firms",
      },
      {
        step: "After Graduation",
        detail: "Join law firm, litigation, or corporate legal",
      },
      { step: "10+ Years", detail: "Senior Advocate, Judge, or own law firm" },
    ],
    growthOutlook: "India's legal market booming in corporate and IP law.",
    whyMatch:
      "Your analytical mind and social awareness make you a natural advocate for justice.",
  },
  {
    id: "architect",
    title: "Architect",
    emoji: "🏗️",
    tagline: "Design buildings that inspire and endure",
    color: "#636E72",
    traits: {
      creative: 0.32,
      technical: 0.25,
      analytical: 0.2,
      business: 0.13,
      social: 0.1,
    },
    description:
      "Architects design the physical world — homes, offices, cities, monuments. It's the perfect blend of art and engineering, creativity and mathematics.",
    whatTheyDo: [
      "Design buildings, interiors, and landscapes",
      "Create blueprints and 3D models",
      "Work with engineers and construction teams",
      "Ensure structural safety and aesthetics",
      "Manage projects from concept to completion",
    ],
    skills: [
      "Drawing & Sketching",
      "AutoCAD / SketchUp / Revit",
      "Mathematics",
      "Physics basics",
      "3D Visualization",
      "Creativity + Precision",
    ],
    salaryRange: {
      entry: "₹3-7 LPA",
      mid: "₹10-25 LPA",
      senior: "₹30-60+ LPA",
    },
    topColleges: [
      "IIT Kharagpur",
      "SPA Delhi",
      "CEPT Ahmedabad",
      "Sir JJ Mumbai",
    ],
    roadmap: [
      { step: "After 10th", detail: "Science (PCM) — Maths is essential" },
      { step: "After 12th", detail: "B.Arch (5 years) via NATA/JEE" },
      {
        step: "During College",
        detail: "Internships at architecture firms, build portfolio",
      },
      {
        step: "After Graduation",
        detail: "Join architecture firm or start practice",
      },
      {
        step: "10+ Years",
        detail: "Senior Architect, own firm, urban planner",
      },
    ],
    growthOutlook:
      "India's construction boom needs 3 lakh+ architects. Smart cities, green buildings driving demand.",
    whyMatch:
      "Your creative vision and technical precision combine perfectly in architecture.",
  },
];

export default careers;
